import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import { api2faVerify, apiLogin, apiLogout, apiRefresh } from '../api/auth';
import { setAuthAccessors } from '../api/client';
import { connectSocket, disconnectSocket, setSocketTokenProvider } from '../api/socket';

type AuthState =
  | { status: 'loading' }
  | { status: 'unauthenticated' }
  | { status: 'pending_2fa'; pendingToken: string; username: string }
  | { status: 'setup_required'; pendingToken: string; username: string }
  | { status: 'authenticated'; username: string; accessToken: string; permissions: string[] };

interface AuthContextValue {
  auth: AuthState;
  login: (username: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
  verify2fa: (code: string, type?: 'totp' | 'recovery') => Promise<void>;
  updateAccessToken: (token: string) => void;
}

const AuthContext = createContext<AuthContextValue | null>(null);

interface DecodedToken { username: string; permissions: string[] }

function decodeAccessToken(token: string): DecodedToken {
  try {
    const payload = JSON.parse(atob(token.split('.')[1])) as {
      sub?: string;
      permissions?: string[];
    };
    return {
      username: payload.sub ?? 'unknown',
      permissions: payload.permissions ?? [],
    };
  } catch {
    return { username: 'unknown', permissions: [] };
  }
}

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [auth, setAuth] = useState<AuthState>({ status: 'loading' });
  const loggingOutRef = useRef(false);

  const updateAccessToken = useCallback((token: string) => {
    setAuth(prev => {
      if (prev.status !== 'authenticated') return prev;
      const { permissions } = decodeAccessToken(token);
      return { ...prev, accessToken: token, permissions };
    });
  }, []);

  const logout = useCallback(async () => {
    if (loggingOutRef.current) return;
    loggingOutRef.current = true;
    setAuth({ status: 'unauthenticated' });
    try { await apiLogout(); } catch { /* ignore */ }
    loggingOutRef.current = false;
  }, []);

  // Keep axios interceptors and socket in sync with current auth state.
  useEffect(() => {
    const getToken = () => (auth.status === 'authenticated' ? auth.accessToken : null);
    setAuthAccessors(getToken, logout, updateAccessToken);
    setSocketTokenProvider(getToken);
    if (auth.status === 'authenticated') {
      connectSocket();
    } else if (auth.status === 'unauthenticated') {
      disconnectSocket();
    }
  }, [auth, logout, updateAccessToken]);

  // Attempt silent refresh on mount (restore prior session).
  // Retries up to 3 times on transient gateway errors (502/503) so a slow
  // backend start doesn't boot the user to the login page.
  useEffect(() => {
    const tryRefresh = (attemptsLeft: number) => {
      apiRefresh()
        .then(({ accessToken }) => {
          const { username, permissions } = decodeAccessToken(accessToken);
          setAuth({ status: 'authenticated', username, accessToken, permissions });
        })
        .catch((err: unknown) => {
          const status = (err as { response?: { status?: number } })?.response?.status;
          const isTransient = !status || status >= 502;
          if (attemptsLeft > 0 && isTransient) {
            setTimeout(() => tryRefresh(attemptsLeft - 1), 2000);
          } else {
            setAuth({ status: 'unauthenticated' });
          }
        });
    };
    tryRefresh(3);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const login = useCallback(async (username: string, password: string) => {
    const result = await apiLogin(username, password);
    if (result.status === 'ok') {
      const decoded = decodeAccessToken(result.accessToken);
      setAuth({ status: 'authenticated', username: decoded.username, accessToken: result.accessToken, permissions: decoded.permissions });
    } else if (result.status === '2fa_required') {
      setAuth({ status: 'pending_2fa', pendingToken: result.pendingToken, username });
    } else {
      setAuth({ status: 'setup_required', pendingToken: result.pendingToken, username });
    }
  }, []);

  const verify2fa = useCallback(async (code: string, type: 'totp' | 'recovery' = 'totp') => {
    const pendingToken =
      auth.status === 'pending_2fa' || auth.status === 'setup_required'
        ? auth.pendingToken
        : null;
    if (!pendingToken) throw new Error('No pending authentication');
    const result = await api2faVerify(pendingToken, code, type);
    const { username, permissions } = decodeAccessToken(result.accessToken);
    setAuth({ status: 'authenticated', username, accessToken: result.accessToken, permissions });
  }, [auth]);

  return (
    <AuthContext.Provider value={{ auth, login, logout, verify2fa, updateAccessToken }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}
